import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import { AppState, Chat, MessageOrder, GroupProgress, PdfAttachment, Toast } from './types';
import { v4 as uuidv4 } from 'uuid';
import { debugLogger } from '../utils/debugLogger';

const SOCKET_SERVER_URL = (import.meta as any).env.VITE_BACKEND_URL;

const initialGroupProgress: GroupProgress = {
  current: 0,
  total: 0,
  percentage: 0,
  status: '',
  successfulGroups: [],
  failedGroups: []
};

// Initialize authId once to prevent re-execution
const getOrCreateAuthId = () => {
  let storedUUID = localStorage.getItem("wpp_auth_uuid");
  if (!storedUUID) {
    const newUUID = uuidv4();
    localStorage.setItem("wpp_auth_uuid", newUUID);
    storedUUID = newUUID;
  }
  return storedUUID;
};

// Connection state recovery utilities
const CONNECTION_STATE_KEY = 'wpp_connection_state';
const RATE_LIMIT_KEY = 'wpp_rate_limit_state';

const saveConnectionState = (isConnected: boolean) => {
  const state = {
    isConnected,
    timestamp: Date.now()
  };
  localStorage.setItem(CONNECTION_STATE_KEY, JSON.stringify(state));
};

const loadConnectionState = (): { isConnected: boolean; isRecent: boolean } => {
  try {
    const stored = localStorage.getItem(CONNECTION_STATE_KEY);
    if (!stored) return { isConnected: false, isRecent: false };
    
    const state = JSON.parse(stored);
    const now = Date.now();
    const isRecent = (now - state.timestamp) < 300000; // 5 minutes
    
    return {
      isConnected: state.isConnected && isRecent,
      isRecent
    };
  } catch {
    return { isConnected: false, isRecent: false };
  }
};

// Rate limiting utilities
const saveRateLimitState = (until: number) => {
  const state = {
    rateLimitedUntil: until,
    timestamp: Date.now()
  };
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(state));
};

const getRateLimitState = (): { isRateLimited: boolean; waitTime: number } => {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    if (!stored) return { isRateLimited: false, waitTime: 0 };
    
    const state = JSON.parse(stored);
    const now = Date.now();
    const waitTime = Math.max(0, state.rateLimitedUntil - now);
    
    return {
      isRateLimited: waitTime > 0,
      waitTime
    };
  } catch {
    return { isRateLimited: false, waitTime: 0 };
  }
};

const clearRateLimitState = () => {
  localStorage.removeItem(RATE_LIMIT_KEY);
};

// Last broadcast persistence (so users can retry failed/pending groups after refresh)
const LAST_BROADCAST_KEY = 'wpp_last_broadcast';
const LAST_BROADCAST_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

interface PersistedBroadcast {
  selectedChats: string[];
  messageOrder: MessageOrder[];
  messageText: string;
  groupProgress: GroupProgress;
  timestamp: number;
}

const saveLastBroadcast = (data: PersistedBroadcast) => {
  try {
    localStorage.setItem(LAST_BROADCAST_KEY, JSON.stringify(data));
  } catch {
    // Quota or serialization error - non-fatal, retry simply won't survive refresh
  }
};

const loadLastBroadcast = (): PersistedBroadcast | null => {
  try {
    const stored = localStorage.getItem(LAST_BROADCAST_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored) as PersistedBroadcast;
    if (Date.now() - data.timestamp > LAST_BROADCAST_MAX_AGE_MS) {
      localStorage.removeItem(LAST_BROADCAST_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const clearLastBroadcast = () => {
  localStorage.removeItem(LAST_BROADCAST_KEY);
};

const persistedBroadcast = loadLastBroadcast();

export const useAppStore = create<AppState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state with recovery
      isConnected: loadConnectionState().isConnected,
      isLoading: false,
      qrCode: null,
      authId: getOrCreateAuthId(),

      // Chat state
      allChats: [],
      selectedChats: [],
      searchQuery: '',
      filteredChats: [],

      // Message state - text persists across refresh; File objects can't be persisted in localStorage
      messageText: persistedBroadcast?.messageText ?? '',
      messageOrder: persistedBroadcast?.messageOrder ?? [],
      pdfFiles: [],
      audioFile: null,
      imageFile: null,

      // Progress state - restore last broadcast so user can retry failed/pending groups after refresh
      isSending: false,
      groupProgress: persistedBroadcast?.groupProgress ?? initialGroupProgress,
      lastBroadcastChats: persistedBroadcast?.selectedChats ?? [],

      // Toast state
      toasts: [],

      // Connection actions with stability checking
      setConnectionStatus: (isConnected: boolean) => {
        const { authId } = get();
        debugLogger.connectionEvent('setConnectionStatus called', isConnected, authId);
        
        if (isConnected) {
          // For connections, implement a stability check delay
          debugLogger.connectionEvent('connection pending verification', true, authId);
          
          // Wait 3 seconds before marking as truly connected
          setTimeout(async () => {
            try {
              // Verify connection is still valid before setting
              const response = await fetch(`${SOCKET_SERVER_URL}/api/whatsapp/${authId}/status`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
              });

              if (response.ok) {
                const data = await response.json();
                
                if (data.success && data.data?.status === 'connected') {
                  debugLogger.connectionEvent('connection verified and confirmed', true, authId);
                  saveConnectionState(true);
                  set({ isConnected: true }, false, 'setConnectionStatus/verified');
                } else {
                  debugLogger.connectionEvent('connection verification failed', false, authId, { 
                    response: data 
                  });
                }
              } else {
                debugLogger.connectionEvent('connection verification request failed', false, authId, { 
                  status: response.status 
                });
              }
            } catch (error) {
              debugLogger.error('CONNECTION', 'verification request error', error, authId);
            }
          }, 3000);
        } else {
          // For disconnections, set immediately
          debugLogger.connectionEvent('disconnection immediate', false, authId);
          saveConnectionState(false);
          set({ isConnected: false }, false, 'setConnectionStatus/disconnected');
        }
      },

      setLoading: (loading: boolean) => 
        set({ isLoading: loading }, false, 'setLoading'),

      setQRCode: (qr: string | null) => 
        set({ qrCode: qr }, false, 'setQRCode'),

      setAuthId: (authId: string) => {
        localStorage.setItem("wpp_auth_uuid", authId);
        set({ authId }, false, 'setAuthId');
      },

      // Chat actions
      setAllChats: (chats: Chat[]) => {
        const { searchQuery } = get();
        const filteredChats = searchQuery 
          ? chats.filter(chat => 
              chat.subject.toLowerCase().includes(searchQuery.toLowerCase())
            )
          : chats;
        
        set({ 
          allChats: chats, 
          filteredChats 
        }, false, 'setAllChats');
      },

      setSelectedChats: (chats: string[]) => 
        set({ selectedChats: chats }, false, 'setSelectedChats'),

      toggleChatSelection: (chatId: string) => {
        const { selectedChats } = get();
        const newSelection = selectedChats.includes(chatId)
          ? selectedChats.filter(id => id !== chatId)
          : [...selectedChats, chatId];
        
        set({ selectedChats: newSelection }, false, 'toggleChatSelection');
      },

      selectAllFilteredChats: () => {
        const { filteredChats, selectedChats } = get();
        const filteredChatIds = filteredChats.map(chat => chat.id);
        const newSelectedChats = [...new Set([...selectedChats, ...filteredChatIds])];
        set({ selectedChats: newSelectedChats }, false, 'selectAllFilteredChats');
      },

      deselectAllFilteredChats: () => {
        const { filteredChats, selectedChats } = get();
        const filteredChatIds = filteredChats.map(chat => chat.id);
        const newSelectedChats = selectedChats.filter(id => !filteredChatIds.includes(id));
        set({ selectedChats: newSelectedChats }, false, 'deselectAllFilteredChats');
      },

      clearSelection: () => 
        set({ selectedChats: [] }, false, 'clearSelection'),

      setSearchQuery: (query: string) => {
        const { allChats } = get();
        const filteredChats = query 
          ? allChats.filter(chat => 
              chat.subject.toLowerCase().includes(query.toLowerCase())
            )
          : allChats;
        
        set({ 
          searchQuery: query, 
          filteredChats 
        }, false, 'setSearchQuery');
      },

      refreshChats: async (forceLoad?: boolean) => {
        forceLoad = forceLoad ?? false;
        const { authId, isLoading } = get();
        
        // Prevent multiple concurrent requests
        if (isLoading) {
          debugLogger.groupsEvent('refresh skipped - already loading', undefined, authId);
          return;
        }
        
        // Validate prerequisites
        if (!authId) {
          get().showToast({
            type: 'error',
            title: 'Erro de Autenticação',
            message: 'AuthId não encontrado. Reconecte ao WhatsApp.',
            duration: 5000
          });
          return;
        }

        // Check if we're currently rate limited
        const rateLimitState = getRateLimitState();
        if (rateLimitState.isRateLimited && !forceLoad) {
          const waitMinutes = Math.ceil(rateLimitState.waitTime / 60000);
          
          debugLogger.warn('GROUPS', 'refresh blocked by rate limit', {
            waitTime: rateLimitState.waitTime,
            waitMinutes
          }, authId);
          
          get().showToast({
            type: 'warning',
            title: 'Rate Limit Ativo',
            message: `Aguarde ${waitMinutes} minuto(s) antes de tentar carregar grupos novamente.`,
            duration: 8000,
            action: {
              label: 'Forçar Mesmo Assim',
              onClick: () => get().refreshChats(true)
            }
          });
          return;
        }
        
        debugLogger.groupsEvent('refreshChats started', undefined, authId, { forceLoad, wasRateLimited: rateLimitState.isRateLimited });
        
        set({ isLoading: true }, false, 'refreshChats/start');
        
        // Show loading toast for long operations
        const loadingToastId = `loading-${Date.now()}`;
        get().showToast({
          type: 'info',
          title: 'Carregando Grupos',
          message: 'Atualizando lista de grupos do WhatsApp...',
          duration: 0 // Don't auto-hide
        });

        // Enhanced timeout mechanism with guaranteed cleanup
        let isTimeoutTriggered = false;
        const loadingTimeout = setTimeout(() => {
          if (!isTimeoutTriggered) {
            isTimeoutTriggered = true;
            console.warn('⏰ Loading timeout reached, resetting loading state');
            set({ isLoading: false }, false, 'refreshChats/timeout');
            get().hideToast(loadingToastId);
            get().showToast({
              type: 'warning',
              title: 'Timeout',
              message: 'Carregamento demorou muito. Tente novamente.',
              duration: 5000
            });
          }
        }, 30000); // Reduced to 30 seconds

        // Cleanup function to ensure loading state is always reset
        const cleanup = () => {
          if (!isTimeoutTriggered) {
            clearTimeout(loadingTimeout);
            set({ isLoading: false }, false, 'refreshChats/cleanup');
            get().hideToast(loadingToastId);
          }
        };

        // Force cleanup after 45 seconds as absolute failsafe
        const emergencyCleanup = setTimeout(() => {
          console.warn('🚨 Emergency cleanup triggered for refreshChats');
          set({ isLoading: false }, false, 'refreshChats/emergency');
          get().hideToast(loadingToastId);
        }, 45000);
        
        try {
          // Exponential backoff retry logic
          const maxRetries = 3;
          let lastError: Error = new Error('Unknown error');
        
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout
            
            const res = await fetch(`${SOCKET_SERVER_URL}/api/messaging/reload-chats`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ authId }),
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            // Handle rate limiting with persistent storage
            if (res.status === 429) {
              const retryAfter = res.headers.get('Retry-After');
              const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 60000;
              
              // Save rate limit state persistently
              saveRateLimitState(Date.now() + waitTime);
              
              debugLogger.warn('GROUPS', 'rate limited by server', {
                retryAfter,
                waitTime,
                attempt: attempt + 1,
                maxRetries
              }, authId);
              
              if (attempt === maxRetries) {
                const waitMinutes = Math.ceil(waitTime / 60000);
                throw new Error(`Rate limited. Aguarde ${waitMinutes} minuto(s) antes de tentar novamente.`);
              }
              
              const waitSeconds = Math.ceil(waitTime / 1000);
              get().showToast({
                type: 'warning',
                title: 'Limite de Taxa',
                message: `Servidor limitou requisições. Aguardando ${waitSeconds}s...`,
                duration: Math.min(waitTime, 10000)
              });
              
              // Wait for the full time before retrying
              await new Promise(resolve => setTimeout(resolve, waitTime));
              continue;
            }
            
            if (!res.ok) {
              const errorText = await res.text().catch(() => 'Unknown error');
              throw new Error(`HTTP ${res.status}: ${errorText}`);
            }
            
            const data = await res.json();
            
            if (!data.success) {
              throw new Error(data.message || 'Resposta inválida do servidor');
            }
            
            const chats = data.data?.chats || [];
            const count = data.data?.count || chats.length;
            
            // Update state
            get().setAllChats(chats);
            
            // Clear rate limit state on successful request
            clearRateLimitState();
            
            // Use cleanup function for consistent state management
            cleanup();
            
            // Show success message
            get().showToast({
              type: 'success',
              title: 'Grupos Carregados',
              message: `${count} grupos carregados com sucesso.`,
              duration: 4000
            });
            
            debugLogger.groupsEvent('refreshChats completed', count, authId);
            return; // Success, exit retry loop
            
          } catch (err) {
            lastError = err as Error;
            console.error(`❌ Attempt ${attempt + 1} failed:`, err);
            
            // Don't retry on abort (timeout) or certain errors
            if (err instanceof Error) {
              if (err.name === 'AbortError') {
                lastError = new Error('Timeout: A operação demorou mais que 30 segundos');
                break;
              }
              if (err.message.includes('401') || err.message.includes('403')) {
                lastError = new Error('Não autorizado. Reconecte ao WhatsApp.');
                break;
              }
            }
            
            // Wait before retry (except on last attempt)
            if (attempt < maxRetries) {
              const delay = Math.min(1000 * Math.pow(2, attempt) + Math.random() * 1000, 10000);
              console.log(`⏳ Waiting ${delay}ms before retry ${attempt + 2}/${maxRetries + 1}`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
        
        // All retries failed
        debugLogger.error('GROUPS', 'refreshChats failed after retries', { 
          error: lastError.message,
          attempts: maxRetries + 1
        }, authId);
        
        // Use cleanup function for consistent state management
        cleanup();
        
        // Determine error message
        let errorMessage = 'Não foi possível carregar os grupos.';
        let actionButton: { label: string; onClick: () => void } | undefined;
        
        if (lastError?.message.includes('Rate limited')) {
          errorMessage = lastError.message;
        } else if (lastError?.message.includes('Timeout')) {
          errorMessage = 'Operação demorou muito. Verifique sua conexão.';
        } else if (lastError?.message.includes('Não autorizado')) {
          errorMessage = 'Sessão expirada. Reconecte ao WhatsApp.';
          actionButton = {
            label: 'Reconectar',
            onClick: () => {
              localStorage.removeItem('wpp_auth_uuid');
              window.location.reload();
            }
          };
        } else if (lastError?.message.includes('NetworkError') || lastError?.message.includes('fetch')) {
          errorMessage = 'Erro de conexão. Verifique sua internet.';
        }
        
        get().showToast({
          type: 'error',
          title: 'Falha ao Carregar Grupos',
          message: errorMessage,
          duration: 8000,
          action: actionButton || {
            label: 'Tentar Novamente',
            onClick: () => {
              // Use a timeout to avoid recursive call issues
              setTimeout(() => get().refreshChats(true), 100);
            }
          }
        });
        } finally {
          // Always clear emergency cleanup timer
          clearTimeout(emergencyCleanup);
        }
      },

      // Message actions
      setMessageText: (text: string) => 
        set({ messageText: text }, false, 'setMessageText'),

      setMessageOrder: (order: MessageOrder[]) => 
        set({ messageOrder: order }, false, 'setMessageOrder'),

      addToMessageOrder: (item: MessageOrder) => {
        const { messageOrder } = get();
        
        // Validar antes de adicionar
        if (item.type === 'text' && (!item.content || !item.content.trim())) return;
        if (item.type === 'pdf' && (!item.fileId || !get().pdfFiles.some(p => p.id === item.fileId))) return;
        if (item.type === 'audio' && !get().audioFile) return;
        if (item.type === 'image' && !get().imageFile) return;
        
        set({ 
          messageOrder: [...messageOrder, item] 
        }, false, 'addToMessageOrder');
      },

      removeFromMessageOrder: (index: number) => {
        const { messageOrder } = get();
        const newOrder = messageOrder.filter((_, i) => i !== index);
        set({ messageOrder: newOrder }, false, 'removeFromMessageOrder');
      },

      moveInMessageOrder: (fromIndex: number, toIndex: number) => {
        const { messageOrder } = get();
        const newOrder = [...messageOrder];
        const [movedItem] = newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, movedItem);
        set({ messageOrder: newOrder }, false, 'moveInMessageOrder');
      },

      clearMessageOrder: () =>
        set({
          messageOrder: [],
          pdfFiles: [],
          audioFile: null,
          imageFile: null,
          messageText: ''
        }, false, 'clearMessageOrder'),

      addPdfFile: (file: File) => {
        const attachment: PdfAttachment = { id: uuidv4(), file };
        const { pdfFiles, messageOrder } = get();
        // A sequência restaurada do localStorage (pós-refresh) perde os File
        // objects. Religa itens órfãos com o mesmo nome a este arquivo, para o
        // "tentar novamente" continuar funcionando.
        const loadedIds = new Set(pdfFiles.map(p => p.id));
        const rebound = messageOrder.map(item =>
          item.type === 'pdf' && item.content === file.name && (!item.fileId || !loadedIds.has(item.fileId))
            ? { ...item, fileId: attachment.id }
            : item
        );
        set({ pdfFiles: [...pdfFiles, attachment], messageOrder: rebound }, false, 'addPdfFile');
        return attachment;
      },

      removePdfFile: (id: string) => {
        const { pdfFiles, messageOrder } = get();
        set({
          pdfFiles: pdfFiles.filter(p => p.id !== id),
          messageOrder: messageOrder.filter(item => !(item.type === 'pdf' && item.fileId === id))
        }, false, 'removePdfFile');
      },

      setAudioFile: (file: File | null) =>
        set({ audioFile: file }, false, 'setAudioFile'),

      setImageFile: (file: File | null) =>
        set({ imageFile: file }, false, 'setImageFile'),

      // Progress actions
      setSending: (sending: boolean) => 
        set({ isSending: sending }, false, 'setSending'),

      setGroupProgress: (progress: GroupProgress) => {
        set({ groupProgress: progress }, false, 'setGroupProgress');

        // Persist after each socket update so refresh/crash mid-broadcast doesn't lose progress
        const { lastBroadcastChats, messageOrder, messageText } = get();
        if (lastBroadcastChats.length > 0) {
          saveLastBroadcast({
            selectedChats: lastBroadcastChats,
            messageOrder,
            messageText,
            groupProgress: progress,
            timestamp: Date.now()
          });
        }
      },

      resetProgress: () => {
        set({
          isSending: false,
          groupProgress: initialGroupProgress,
          lastBroadcastChats: []
        }, false, 'resetProgress');
        clearLastBroadcast();
      },

      retryFailedAndPending: () => {
        const { lastBroadcastChats, groupProgress } = get();
        const sent = new Set(groupProgress.successfulGroups);
        const missing = lastBroadcastChats.filter(id => !sent.has(id));
        set({
          selectedChats: missing,
          groupProgress: initialGroupProgress,
          lastBroadcastChats: [],
          isSending: false
        }, false, 'retryFailedAndPending');
        clearLastBroadcast();
        return missing.length;
      },

      // Toast actions
      showToast: (toastData: Omit<Toast, 'id'>) => {
        const toast: Toast = {
          id: uuidv4(),
          duration: 5000, // 5 seconds default
          ...toastData
        };
        
        const { toasts } = get();
        set({ toasts: [...toasts, toast] }, false, 'showToast');

        // Auto-hide toast after duration
        if (toast.duration && toast.duration > 0) {
          setTimeout(() => {
            get().hideToast(toast.id);
          }, toast.duration);
        }
      },

      hideToast: (id: string) => {
        const { toasts } = get();
        set({ toasts: toasts.filter(toast => toast.id !== id) }, false, 'hideToast');
      },

      clearAllToasts: () => 
        set({ toasts: [] }, false, 'clearAllToasts'),

      // Complex actions
      sendMessages: async () => {
        const { 
          selectedChats, 
          messageOrder, 
          messageText, 
          pdfFiles, 
          audioFile, 
          authId 
        } = get();

        if (!selectedChats.length) {
          get().showToast({
            type: 'warning',
            title: 'Seleção de Chats',
            message: 'Selecione pelo menos um chat para enviar mensagens.'
          });
          return;
        }

        // Filtrar ordem para garantir que não há elementos vazios
        const filteredOrder = messageOrder.filter(item => {
          if (item.type === 'text') return item.content && item.content.trim();
          if (item.type === 'pdf') return !!item.fileId && pdfFiles.some(p => p.id === item.fileId);
          if (item.type === 'audio') return !!audioFile;
          if (item.type === 'image') return !!get().imageFile;
          return false;
        });

        if (filteredOrder.length === 0) {
          get().showToast({
            type: 'warning',
            title: 'Mensagem Vazia',
            message: 'Adicione pelo menos um elemento válido (texto, PDF, áudio ou imagem).'
          });
          return;
        }

        const formData = new FormData();
        formData.append("messageOrder", JSON.stringify(filteredOrder));
        // Um campo por PDF (`pdfFile:<id>`): o backend casa cada item da ordem
        // com o seu próprio arquivo, em vez de reaproveitar um único buffer.
        const usedPdfIds = new Set(filteredOrder.filter(i => i.type === 'pdf').map(i => i.fileId));
        pdfFiles
          .filter(p => usedPdfIds.has(p.id))
          .forEach(p => formData.append(`pdfFile:${p.id}`, p.file, p.file.name));
        if (audioFile) formData.append("audioFile", audioFile);
        if (get().imageFile) formData.append("imageFile", get().imageFile as File);
        if (messageText.trim()) formData.append("text", messageText);
        formData.append("authId", authId);
        
        selectedChats.forEach((item) => {
          formData.append(`chats[]`, item);
        });

        console.log('🚀 Iniciando envio de mensagens para grupos');
        
        const initialProgress = {
          current: 0,
          total: selectedChats.length,
          percentage: 0,
          status: "Iniciando envio...",
          successfulGroups: [] as string[],
          failedGroups: [] as Array<{ group: string; error: string }>
        };

        set({
          isSending: true,
          groupProgress: initialProgress,
          lastBroadcastChats: [...selectedChats]
        }, false, 'sendMessages/start');

        // Persist the snapshot immediately so a crash before any socket event still leaves retry data
        saveLastBroadcast({
          selectedChats: [...selectedChats],
          messageOrder: filteredOrder,
          messageText,
          groupProgress: initialProgress,
          timestamp: Date.now()
        });

        try {
          const res = await fetch(`${SOCKET_SERVER_URL}/api/messaging/send-groups`, {
            method: "POST",
            body: formData,
          });
          
          if (res.ok) {
            console.log('✅ Requisição enviada com sucesso, aguardando progresso via socket');
            get().showToast({
              type: 'success',
              title: 'Envio Iniciado',
              message: 'Mensagens enviadas com sucesso! Acompanhe o progresso em tempo real.'
            });
          } else {
            console.error('❌ Erro na requisição:', res.status);
            get().showToast({
              type: 'error',
              title: 'Erro no Envio',
              message: `Falha ao enviar mensagens. Código: ${res.status}`
            });
            get().resetProgress();
          }
        } catch (error) {
          console.error('❌ Erro na requisição:', error);
          get().showToast({
            type: 'error',
            title: 'Erro de Conexão',
            message: 'Falha na conexão com o servidor. Verifique sua internet.'
          });
          get().resetProgress();
        }
      },

      logout: async () => {
        const { authId } = get();
        
        debugLogger.connectionEvent('logout initiated', false, authId);
        
        // Immediately clear connection state
        set({ 
          isConnected: false,
          qrCode: null 
        }, false, 'logout/immediate');
        
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);
          
          const response = await fetch(`${SOCKET_SERVER_URL}/api/whatsapp/logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ authId }),
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            debugLogger.connectionEvent('backend logout successful', false, authId);
          } else {
            debugLogger.warn('LOGOUT', 'backend logout failed but continuing', { 
              status: response.status 
            }, authId);
          }
        } catch (error) {
          debugLogger.error('LOGOUT', 'backend logout error', error, authId);
        }

        // Comprehensive local cleanup
        localStorage.removeItem('wpp_auth_uuid');
        localStorage.removeItem('wpp_connection_state');
        clearLastBroadcast();
        clearRateLimitState(); // Clear rate limiting when logging out
        sessionStorage.clear(); // Clear session storage too
        
        set({
          allChats: [],
          selectedChats: [],
          qrCode: null,
          authId: "",
          isConnected: false,
          isLoading: false,
          searchQuery: '',
          filteredChats: [],
          messageOrder: [],
          messageText: '',
          pdfFiles: [],
          audioFile: null,
          imageFile: null,
          isSending: false,
          groupProgress: initialGroupProgress,
          lastBroadcastChats: [],
          toasts: [] // Clear toasts too
        }, false, 'logout/complete');

        debugLogger.connectionEvent('logout completed', false, authId);
        
        // Force reload after small delay to ensure state is cleared
        setTimeout(() => {
          window.location.reload();
        }, 100);
      },

      // Utils
      getGroupName: (groupId: string): string => {
        const { allChats } = get();
        const chat = allChats.find(chat => chat.id === groupId);
        return chat ? chat.subject : groupId;
      }
    })),
    {
      name: 'app-store'
    }
  )
);

// Selectors para performance (evitar re-renders desnecessários)
export const useConnectionState = () => useAppStore(
  useShallow((state) => ({
    isConnected: state.isConnected,
    isLoading: state.isLoading,
    qrCode: state.qrCode,
    authId: state.authId,
    setConnectionStatus: state.setConnectionStatus,
    setLoading: state.setLoading,
    setQRCode: state.setQRCode,
    setAuthId: state.setAuthId
  }))
);

export const useChatState = () => useAppStore(
  useShallow((state) => ({
    allChats: state.allChats,
    selectedChats: state.selectedChats,
    searchQuery: state.searchQuery,
    filteredChats: state.filteredChats,
    setAllChats: state.setAllChats,
    setSelectedChats: state.setSelectedChats,
    toggleChatSelection: state.toggleChatSelection,
    selectAllFilteredChats: state.selectAllFilteredChats,
    deselectAllFilteredChats: state.deselectAllFilteredChats,
    clearSelection: state.clearSelection,
    setSearchQuery: state.setSearchQuery,
    refreshChats: state.refreshChats
  }))
);

export const useMessageState = () => useAppStore(
  useShallow((state) => ({
    messageText: state.messageText,
    messageOrder: state.messageOrder,
    pdfFiles: state.pdfFiles,
    audioFile: state.audioFile,
    imageFile: state.imageFile,
    setMessageText: state.setMessageText,
    setMessageOrder: state.setMessageOrder,
    addToMessageOrder: state.addToMessageOrder,
    removeFromMessageOrder: state.removeFromMessageOrder,
    moveInMessageOrder: state.moveInMessageOrder,
    clearMessageOrder: state.clearMessageOrder,
    addPdfFile: state.addPdfFile,
    removePdfFile: state.removePdfFile,
    setAudioFile: state.setAudioFile,
    setImageFile: state.setImageFile,
    sendMessages: state.sendMessages
  }))
);

export const useProgressState = () => useAppStore(
  useShallow((state) => ({
    isSending: state.isSending,
    groupProgress: state.groupProgress,
    lastBroadcastChats: state.lastBroadcastChats,
    setSending: state.setSending,
    setGroupProgress: state.setGroupProgress,
    resetProgress: state.resetProgress,
    retryFailedAndPending: state.retryFailedAndPending,
    getGroupName: state.getGroupName
  }))
);

export const useToastState = () => useAppStore(
  useShallow((state) => ({
    toasts: state.toasts,
    showToast: state.showToast,
    hideToast: state.hideToast,
    clearAllToasts: state.clearAllToasts
  }))
);